// Considered: ApproachExplorerOrganism — rejected as a literal wrapper. Its tree is built by
// `buildApproachTree(approach, arcTemplate, …)` from an `ApproachDefinition`'s fixed categories
// (types/contexts/flow/children/factors/orbit/inputs/subapproaches) and its `dataSource` contract
// is Approach-specific (`fetchApproach`/`resolveTypes`/`resolveContexts`). The registry tree here
// is a flat, caller-supplied list of catalog + composition entries with no Approach shape to walk
// — reusing the organism would mean fabricating an `ApproachDefinition` around unrelated data.
import React, { useMemo, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import { useTranslation } from 'localization';
import type { CompositionExample } from 'types/composition';
import type { SpaceDocument } from 'types/space';
import CompositionHost from '../../../composition-host/CompositionHost';
import { useCompositionResolver } from '../../../composition-host/useCompositionResolver';
import type { SpaceAction } from '../../../composition-host/types';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import PrimeCheckbox from '../../atoms/checkbox/Checkbox';
import { StatusBadgeAtom, StatusBadgeStatus } from '../../atoms/StatusBadge';
import { generateKnobsFromInterfaceSchema, extractSlotsFromInterfaceSchema, type KnobDef } from '../../../composer/knobGeneration';
import './RegistryExplorerOrganism.scss';

/** Stored `space_conformance_verdicts` row shape (`GET .../compositions/:name`'s folded-in `verdict`) — never recomputed client-side. */
export interface RegistryEntryVerdict {
  status: 'pass' | 'fail' | 'warn' | 'stale';
  levelsRun: string[];
  findings: unknown[];
  verifiedAt: string;
}

export interface RegistryEntry {
  id: string;
  origin: 'catalog' | 'composition';
  kind: string;
  name: string;
  description?: string;
  interfaceSchema?: Record<string, unknown> | null;
  emits?: string[];
  examples?: CompositionExample[] | null;
  canEdit?: boolean;
  /** `null`/absent for code-backed catalog entries — they have no version concept. */
  version?: number | null;
  /**
   * Stored conformance verdict for this entry's current version — `undefined` until fetched,
   * `null` when the caller fetched and none is stored. `ConformanceBadge` renders only when this
   * is a real object, never for `undefined`/`null`, so it can't act as an existence oracle for a
   * verdict the caller isn't authorized to see (the route itself is `view`-gated).
   */
  verdict?: RegistryEntryVerdict | null;
}

/** Maps a stored verdict status onto `StatusBadgeAtom`'s sphere-variant enum. `warn` renders as
 * `conformance_fail` — the badge has only three conformance states (Phase 5.1) and a warning
 * finding is closer to "needs attention" than to a clean pass. */
function verdictToBadgeStatus(status: RegistryEntryVerdict['status']): StatusBadgeStatus {
  switch (status) {
    case 'pass':
      return 'conformance_pass';
    case 'stale':
      return 'conformance_stale';
    case 'fail':
    case 'warn':
    default:
      return 'conformance_fail';
  }
}

export interface RegistryExplorerOrganismProps {
  entries: RegistryEntry[];
  loading?: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpenInComposer?: (entry: RegistryEntry) => void;
  onFork?: (entry: RegistryEntry) => void;
  /** Absent when the caller lacks `edit` on the selected (composition-origin) entry. */
  onSaveStory?: (entry: RegistryEntry, story: { name: string; description?: string; props: Record<string, unknown> }) => void;
  savingStory?: boolean;
  /**
   * Absent when the caller lacks `edit` on the selected entry, or the entry has no published
   * version yet (nothing to publish against). The caller owns preflight → dialog → publish; this
   * organism only surfaces the request.
   */
  onPublishRequest?: (entry: RegistryEntry) => void;
  publishRequestPending?: boolean;
  /**
   * A publish request the caller could not complete (the preflight read failed), already
   * localized. Rendered next to the Publish button so a click that reached no verdict is never
   * silent; absent means no request has failed.
   */
  publishRequestError?: string;
  /** Absent when not yet loaded/authenticated — the live preview stage renders nothing for composition-origin entries (catalog entries still preview, since they need no fetch). */
  apiClient?: ApiClient;
}

const PREVIEW_SURFACE_ID = 'registry-explorer-preview';
const PREVIEW_ROOT_ID = 'preview-root';
const EMIT_LOG_LIMIT = 20;

// Fixed group order (V3 mockup IA, EAINT-844 Phase F1): `surface`/`section` merge composition
// kinds with catalog roles of the same name (both origins now share the same string), `fragment`
// is composition-only, `primitive` (catalog-only "Display & input" vocabulary) sits last. Any
// unrecognized/missing kind falls back after these, in first-seen order.
const KIND_GROUP_ORDER = ['surface', 'section', 'fragment', 'primitive'] as const;

function buildPropsFromKnobs(knobs: KnobDef[], knobValues: Record<string, unknown>): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  knobs.forEach((knob) => {
    props[knob.name] = knob.name in knobValues ? knobValues[knob.name] : knobDefaultValue(knob);
  });
  return props;
}

function knobDefaultValue(knob: KnobDef): unknown {
  switch (knob.controlType) {
    case 'boolean':
      return false;
    case 'number':
      return 0;
    case 'stringArray':
      return [];
    case 'select':
      return knob.enumOptions?.[0] ?? '';
    default:
      return '';
  }
}

/**
 * The `/compose` Explorer tab (SC6): a permission-filtered, searchable registry tree (code-backed
 * catalog entries + composition-backed entries, grouped by `kind` with origin/version as a
 * per-item badge), a generated docs panel (props/slots/emits/version/used-in from the stored
 * `interfaceSchema`), knob controls generated from that same schema, and a save-as-story action
 * that persists the current knob values as a `CompositionExample`. `entries` is the tree — callers
 * already resolve it permission-filtered.
 */
const RegistryExplorerOrganism: React.FC<RegistryExplorerOrganismProps> = ({
  entries,
  loading = false,
  selectedId,
  onSelect,
  onOpenInComposer,
  onFork,
  onSaveStory,
  savingStory = false,
  onPublishRequest,
  publishRequestPending = false,
  publishRequestError,
  apiClient,
}) => {
  const { t } = useTranslation();
  const [knobValues, setKnobValues] = useState<Record<string, unknown>>({});
  const [storyName, setStoryName] = useState('');
  const [storyDescription, setStoryDescription] = useState('');
  const [emitLog, setEmitLog] = useState<SpaceAction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const selected = useMemo(() => entries.find((e) => e.id === selectedId) ?? null, [entries, selectedId]);
  const knobs = useMemo(() => generateKnobsFromInterfaceSchema(selected?.interfaceSchema), [selected]);
  const slots = useMemo(() => extractSlotsFromInterfaceSchema(selected?.interfaceSchema), [selected]);
  const previewProps = useMemo(() => buildPropsFromKnobs(knobs, knobValues), [knobs, knobValues]);

  // A minimal single-node document — `useCompositionResolver` only reads `a2ui.components` to
  // find composition refs (`findCompositionRefs`), and a catalog-origin entry's `id` matches a
  // registered adapter/catalog type, so it never resolves as a ref in the first place.
  const previewDocument: SpaceDocument | null = useMemo(() => {
    if (!selected) return null;
    return {
      formatVersion: 'space/v1',
      catalogVersion: 1,
      surface: { family: selected.id, componentId: PREVIEW_ROOT_ID, layout: 'uniform' },
      a2ui: {
        version: 'v1.0',
        catalogId: 'center-platform',
        root: PREVIEW_ROOT_ID,
        components: [{ id: PREVIEW_ROOT_ID, component: selected.id, ...previewProps }],
      },
    };
  }, [selected, previewProps]);
  const { resolveComposition, isResolving: previewResolving } = useCompositionResolver(previewDocument, apiClient);

  const previewMessages = useMemo(() => {
    if (!previewDocument) return [];
    return [
      {
        type: 'createSurface' as const,
        surfaceId: PREVIEW_SURFACE_ID,
        components: previewDocument.a2ui.components,
        root: PREVIEW_ROOT_ID,
      },
    ];
  }, [previewDocument]);

  const handlePreviewAction = (action: SpaceAction) => {
    setEmitLog((prev) => [...prev, action].slice(-EMIT_LOG_LIMIT));
  };

  const searchedEntries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return entries;
    return entries.filter(
      (entry) =>
        entry.name.toLowerCase().includes(term) ||
        entry.kind.toLowerCase().includes(term) ||
        (entry.description ?? '').toLowerCase().includes(term)
    );
  }, [entries, searchTerm]);

  // Grouped by `kind` — catalog and composition entries that share a kind (e.g. two
  // "fragment" entries) sit in the same group; origin/version are per-item badges instead
  // of a group split, so the tree reads as one registry rather than two parallel ones.
  const groupedByKind = useMemo(() => {
    const byKind = new Map<string, RegistryEntry[]>();
    searchedEntries.forEach((entry) => {
      const list = byKind.get(entry.kind) ?? [];
      list.push(entry);
      byKind.set(entry.kind, list);
    });
    // Fixed groups first (KIND_GROUP_ORDER), then any unrecognized kind in first-seen order.
    const ordered = new Map<string, RegistryEntry[]>();
    KIND_GROUP_ORDER.forEach((kind) => {
      const list = byKind.get(kind);
      if (list) ordered.set(kind, list);
    });
    byKind.forEach((list, kind) => {
      if (!ordered.has(kind)) ordered.set(kind, list);
    });
    return ordered;
  }, [searchedEntries]);

  // Known kinds have `composer.explorer.group.*` keys; an unknown kind coming back from the
  // registry falls back to its own name via i18next's own missing-key mechanism.
  const groupLabel = (kind: string) => t(`composer.explorer.group.${kind}`, { defaultValue: kind });

  const handleSelect = (entry: RegistryEntry) => {
    setKnobValues({});
    setStoryName('');
    setStoryDescription('');
    setEmitLog([]);
    onSelect(entry.id);
  };

  const handleKnobChange = (name: string, value: unknown) => {
    setKnobValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveStory = () => {
    if (!selected || !onSaveStory || !storyName.trim()) return;
    onSaveStory(selected, {
      name: storyName.trim(),
      description: storyDescription.trim() || undefined,
      props: buildPropsFromKnobs(knobs, knobValues),
    });
    setStoryName('');
    setStoryDescription('');
  };

  const renderTreeGroup = (kind: string, kindEntries: RegistryEntry[]) => (
    <div className="registry-explorer__tree-kind" key={kind}>
      <div className="registry-explorer__tree-kind-label">{groupLabel(kind)}</div>
      <ul className="registry-explorer__tree-list">
        {kindEntries.map((entry) => (
          <li key={entry.id}>
            <button
              type="button"
              className={`registry-explorer__tree-item${entry.id === selectedId ? ' registry-explorer__tree-item--selected' : ''}`}
              onClick={() => handleSelect(entry)}
              aria-current={entry.id === selectedId}
              data-testid={`registry-explorer-entry-${entry.id}`}
            >
              <span className="registry-explorer__tree-item-name">{entry.name}</span>
              <span className="registry-explorer__tree-item-badges">
                <span className="registry-explorer__tree-item-badge">
                  {t(entry.origin === 'catalog' ? 'composer.explorer.backingCatalog' : 'composer.explorer.backingComposition')}
                </span>
                {typeof entry.version === 'number' && (
                  <span className="registry-explorer__tree-item-badge">
                    {t('composer.explorer.version', { version: entry.version })}
                  </span>
                )}
                {entry.verdict && (
                  <StatusBadgeAtom
                    status={verdictToBadgeStatus(entry.verdict.status)}
                    label={t(`composer.explorer.verdictStatus.${entry.verdict.status}`, { defaultValue: entry.verdict.status })}
                    data-testid={`registry-explorer-entry-${entry.id}-verdict`}
                  />
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="registry-explorer" data-testid="registry-explorer">
      <div className="registry-explorer__tree">
        <InputAtom
          className="registry-explorer__search"
          value={searchTerm}
          placeholder={t('composer.explorer.searchPlaceholder')}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label={t('composer.explorer.searchPlaceholder')}
          data-testid="registry-explorer-search"
        />
        {loading ? (
          <p className="registry-explorer__loading">{t('composer.explorer.loading')}</p>
        ) : (
          <>
            {[...groupedByKind.entries()].map(([kind, kindEntries]) => renderTreeGroup(kind, kindEntries))}
            {entries.length === 0 && <p className="registry-explorer__empty">{t('composer.explorer.empty')}</p>}
            {entries.length > 0 && searchedEntries.length === 0 && (
              <p className="registry-explorer__empty">{t('composer.explorer.noResults')}</p>
            )}
          </>
        )}
      </div>

      <div className="registry-explorer__detail">
        {!selected ? (
          <p className="registry-explorer__no-selection">{t('composer.explorer.noSelection')}</p>
        ) : (
          <>
            <div className="registry-explorer__detail-header">
              <h3 className="registry-explorer__detail-name">{selected.name}</h3>
              {/* Translate the kind rather than rendering the raw enum — a French user
                  would otherwise see an English chip beside translated labels. */}
              <ChipAtom label={selected.kind ? t(`composer.kind.${selected.kind}`) : ''} />
              {typeof selected.version === 'number' && (
                <ChipAtom label={t('composer.explorer.version', { version: selected.version })} />
              )}
              {selected.verdict && (
                <StatusBadgeAtom
                  status={verdictToBadgeStatus(selected.verdict.status)}
                  label={t(`composer.explorer.verdictStatus.${selected.verdict.status}`, { defaultValue: selected.verdict.status })}
                  data-testid="registry-explorer-detail-verdict"
                />
              )}
              <div className="registry-explorer__detail-actions">
                {onOpenInComposer && (
                  <ButtonAtom variant="secondary" size="small" onClick={() => onOpenInComposer(selected)}>
                    {t('composer.explorer.openInComposer')}
                  </ButtonAtom>
                )}
                {onFork && (
                  <ButtonAtom variant="text" size="small" onClick={() => onFork(selected)}>
                    {t('composer.explorer.fork')}
                  </ButtonAtom>
                )}
                {onPublishRequest && selected.origin === 'composition' && selected.canEdit && typeof selected.version === 'number' && (
                  <ButtonAtom
                    variant="primary"
                    size="small"
                    disabled={publishRequestPending}
                    loading={publishRequestPending}
                    onClick={() => onPublishRequest(selected)}
                    data-testid="registry-explorer-publish"
                  >
                    {t('composer.explorer.publish', { defaultValue: 'Publish' })}
                  </ButtonAtom>
                )}
              </div>
            </div>
            {publishRequestError && (
              <div className="registry-explorer__publish-error" role="alert" data-testid="registry-explorer-publish-error">
                {publishRequestError}
              </div>
            )}
            {selected.description && <p className="registry-explorer__detail-description">{selected.description}</p>}

            <div className="registry-explorer__preview" data-testid="registry-explorer-preview">
              <h4>{t('composer.explorer.previewHeading')}</h4>
              {previewResolving ? (
                <p className="registry-explorer__preview-loading">{t('composer.explorer.previewResolving')}</p>
              ) : (
                <div className="registry-explorer__preview-stage">
                  <CompositionHost
                    messages={previewMessages}
                    surfaceId={PREVIEW_SURFACE_ID}
                    mode="preview"
                    resolveComposition={resolveComposition}
                    onAction={handlePreviewAction}
                  />
                </div>
              )}
              <div className="registry-explorer__emit-log" data-testid="registry-explorer-emit-log">
                <h5>{t('composer.explorer.emitLogHeading')}</h5>
                {emitLog.length === 0 ? (
                  <p className="registry-explorer__emit-log-empty">{t('composer.explorer.emitLogEmpty')}</p>
                ) : (
                  <ul>
                    {emitLog.map((action, index) => (
                      <li key={`${action.componentId}-${action.name}-${index}`}>
                        {action.componentId} → {action.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="registry-explorer__docs" data-testid="registry-explorer-docs">
              <h4>{t('composer.explorer.docsHeading')}</h4>
              {knobs.length === 0 ? (
                <p className="registry-explorer__docs-empty">{t('composer.explorer.noProps')}</p>
              ) : (
                <table className="registry-explorer__docs-table">
                  <thead>
                    <tr>
                      <th>{t('composer.explorer.propName')}</th>
                      <th>{t('composer.explorer.propType')}</th>
                      <th>{t('composer.explorer.propDescription')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {knobs.map((knob) => (
                      <tr key={knob.name}>
                        <td>{knob.name}{knob.required ? ' *' : ''}</td>
                        <td>{knob.controlType}</td>
                        <td>{knob.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {selected.emits && selected.emits.length > 0 && (
                <div className="registry-explorer__emits">
                  <strong>{t('composer.explorer.emitsHeading')}</strong>{' '}
                  {selected.emits.map((emit) => (
                    <ChipAtom key={emit} label={emit} className="registry-explorer__emit-chip" />
                  ))}
                </div>
              )}

              <div className="registry-explorer__slots" data-testid="registry-explorer-slots">
                <h5>{t('composer.explorer.slotsHeading')}</h5>
                {slots.length === 0 ? (
                  <p className="registry-explorer__docs-empty">{t('composer.explorer.noSlots')}</p>
                ) : (
                  <ul>
                    {slots.map((slot) => (
                      <li key={slot.name}>
                        <code>{slot.name}</code>
                        {slot.description && <span> — {slot.description}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="registry-explorer__used-in" data-testid="registry-explorer-used-in">
                <h5>{t('composer.explorer.usedInHeading')}</h5>
                <p className="registry-explorer__docs-empty">{t('composer.explorer.usedInUnavailable')}</p>
              </div>
            </div>

            {knobs.length > 0 && (
              <div className="registry-explorer__knobs" data-testid="registry-explorer-knobs">
                <h4>{t('composer.explorer.knobsHeading')}</h4>
                {knobs.map((knob) => (
                  <label key={knob.name} className="registry-explorer__knob-row">
                    <span className="registry-explorer__knob-label">{knob.name}</span>
                    {knob.controlType === 'select' ? (
                      <DropdownAtom
                        value={knob.name in knobValues ? knobValues[knob.name] : (knob.enumOptions?.[0] ?? '')}
                        options={(knob.enumOptions ?? []).map((opt) => ({ label: opt, value: opt }))}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => handleKnobChange(knob.name, e.value)}
                      />
                    ) : knob.controlType === 'boolean' ? (
                      <PrimeCheckbox
                        className="registry-explorer__knob-checkbox"
                        checked={Boolean(knobValues[knob.name])}
                        onChange={(next) => handleKnobChange(knob.name, next)}
                      />
                    ) : (
                      <InputAtom
                        type={knob.controlType === 'number' ? 'number' : 'text'}
                        value={String((knobValues[knob.name] as string | number | undefined) ?? '')}
                        onChange={(e) =>
                          handleKnobChange(
                            knob.name,
                            knob.controlType === 'number' ? Number(e.target.value) : e.target.value
                          )
                        }
                      />
                    )}
                  </label>
                ))}
              </div>
            )}

            {onSaveStory && (
              <div className="registry-explorer__save-story" data-testid="registry-explorer-save-story">
                <InputAtom
                  value={storyName}
                  placeholder={t('composer.explorer.storyNamePlaceholder')}
                  onChange={(e) => setStoryName(e.target.value)}
                  aria-label={t('composer.explorer.storyNamePlaceholder')}
                />
                <InputAtom
                  value={storyDescription}
                  placeholder={t('composer.explorer.storyDescriptionPlaceholder')}
                  onChange={(e) => setStoryDescription(e.target.value)}
                  aria-label={t('composer.explorer.storyDescriptionPlaceholder')}
                />
                <ButtonAtom
                  variant="primary"
                  size="small"
                  disabled={!storyName.trim() || savingStory}
                  loading={savingStory}
                  onClick={handleSaveStory}
                >
                  {t('composer.explorer.saveStory')}
                </ButtonAtom>
              </div>
            )}

            {selected.examples && selected.examples.length > 0 && (
              <div className="registry-explorer__stories" data-testid="registry-explorer-stories">
                <h4>{t('composer.explorer.storiesHeading')}</h4>
                <ul>
                  {selected.examples.map((example) => (
                    <li key={example.name}>{example.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RegistryExplorerOrganism;
